// <!-- GRACE:MODULE_START name="ui_landing" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDownIcon,
  CheckCircleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// ─── Data ────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: 'Как работает', href: '#how-it-works' },
  { label: 'Преимущества', href: '#benefits' },
  { label: 'Для кого', href: '#for-whom' },
  { label: 'Отзывы', href: '#testimonials' },
  { label: 'Тарифы', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const STEPS = [
  {
    number: '01',
    title: 'Запишите или загрузите аудио',
    description:
      'Используйте встроенный микрофон или загрузите готовый аудиофайл формата MP3, WAV, M4A или WebM.',
  },
  {
    number: '02',
    title: 'AI распознаёт речь',
    description:
      'Нейросеть обрабатывает запись, точно распознаёт терминологию и структурирует текст.',
  },
  {
    number: '03',
    title: 'Получите готовый документ',
    description:
      'Система применяет выбранный шаблон и форматирует документ, готовый к использованию.',
  },
];

const BENEFITS = [
  {
    icon: '🎯',
    title: 'Высокая точность',
    description:
      'Распознавание специализированных терминов с точностью более 95% благодаря специализированной языковой модели.',
  },
  {
    icon: '⚡',
    title: 'Быстрая обработка',
    description:
      'Аудио длительностью 5 минут обрабатывается менее чем за 30 секунд.',
  },
  {
    icon: '📋',
    title: 'Шаблоны документов',
    description:
      'Гибкая система шаблонов: настройте под свою сферу и получайте документы в нужном формате.',
  },
  {
    icon: '🕐',
    title: 'Длинные записи',
    description:
      'Поддержка записей от 30 до 40 минут и более. Лекции, совещания, длинные интервью — без ограничений по объёму.',
  },
  {
    icon: '🔒',
    title: 'Безопасность данных',
    description:
      'Данные шифруются при передаче и хранении. Соответствие требованиям 152-ФЗ о персональных данных.',
  },
];

const AUDIENCES = [
  {
    emoji: '🩺',
    role: 'Врачи',
    description:
      'Сократите время на ведение документации в 3 раза. Диктуйте прямо во время осмотра — ассистент оформит всё автоматически.',
  },
  {
    emoji: '⚖️',
    role: 'Юристы',
    description:
      'Диктуйте протоколы встреч, переговоров и заседаний. Получайте структурированные документы без ручного набора.',
  },
  {
    emoji: '🎙️',
    role: 'Журналисты',
    description:
      'Расшифровывайте интервью и пресс-конференции мгновенно. Фокусируйтесь на содержании, а не на наборе текста.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Алексей Морозов',
    role: 'Терапевт, Клиника «Здоровье»',
    text: 'Раньше тратил по 2 часа в день на документацию. Сейчас — 20 минут. Толмач буквально вернул мне время для пациентов.',
    rating: 5,
  },
  {
    name: 'Марина Соколова',
    role: 'Юрист, адвокатское бюро',
    text: 'Точность распознавания юридических терминов поразила с первого использования. Протоколы встреч оформляются без правок.',
    rating: 5,
  },
  {
    name: 'Дмитрий Волков',
    role: 'Главный редактор, новостное агентство',
    text: 'Наша редакция расшифровывает по 5–7 интервью в день. Теперь это занимает минуты, а не часы. Незаменимый инструмент.',
    rating: 5,
  },
];

const PRICING = [
  {
    name: 'Транскрибация',
    price: '0 ₽',
    period: 'бесплатно',
    highlight: false,
    badge: null,
    features: [
      'Неограниченная транскрибация речи',
      'Все форматы аудио (MP3, WAV, M4A, WebM…)',
      'Записи до 40 минут и более',
      'Редактирование текста в браузере',
      'Экспорт в TXT',
    ],
    cta: 'Начать бесплатно',
    ctaHref: '/register',
    isExternal: false,
  },
  {
    name: 'AI Обработка',
    price: '490 ₽',
    period: 'в месяц',
    highlight: true,
    badge: 'Популярный выбор',
    features: [
      'Всё из тарифа «Транскрибация»',
      'AI-извлечение фактов и тезисов',
      'AI-форматирование по шаблону',
      'До 1000 AI-обработок в месяц',
      'Экспорт в DOCX и PDF',
    ],
    cta: 'Подключить AI',
    ctaHref: '/register',
    isExternal: false,
  },
  {
    name: 'AI Безлимит',
    price: '990 ₽',
    period: 'в месяц',
    highlight: false,
    badge: null,
    features: [
      'Всё из тарифа «AI Обработка»',
      'Неограниченные AI-обработки',
      'Приоритетная обработка',
      'Выделенная поддержка',
    ],
    cta: 'Выбрать безлимит',
    ctaHref: '/register',
    isExternal: false,
  },
  {
    name: 'Enterprise',
    price: 'По запросу',
    period: '',
    highlight: false,
    badge: null,
    features: [
      'Всё из тарифа «AI Безлимит»',
      'Кастомные шаблоны документов',
      'Интеграция с вашим API',
      'Командный доступ',
      'SLA 99.9%',
      'Персональный менеджер',
    ],
    cta: 'Связаться с нами',
    ctaHref: 'mailto:support@example.com',
    isExternal: true,
  },
];

const FAQ_ITEMS = [
  {
    q: 'Какие форматы аудио поддерживаются?',
    a: 'Поддерживаются MP3, WAV, M4A, OGG, WebM и FLAC (из WebM-видео, например записи созвона, берётся звуковая дорожка). Максимальный размер файла — 500 МБ, максимальная длительность — 3 часа.',
  },
  {
    q: 'Насколько точно распознаётся специализированная терминология?',
    a: 'Точность на специализированных текстах составляет более 95%. Система обучена на профессиональных корпусах документов и регулярно обновляется.',
  },
  {
    q: 'Как обеспечивается безопасность персональных данных?',
    a: 'Все данные шифруются по протоколу TLS 1.3 при передаче и AES-256 при хранении. Аудиофайлы удаляются с серверов через 24 часа после обработки. Соответствие 152-ФЗ.',
  },
  {
    q: 'Можно ли создать собственные шаблоны документов?',
    a: 'Да, в тарифе Pro и Enterprise вы можете создавать неограниченное количество собственных шаблонов. В тарифе Enterprise также доступна профессиональная настройка.',
  },
  {
    q: 'Есть ли мобильная версия?',
    a: 'Веб-версия полностью адаптирована для мобильных устройств. Нативные приложения для iOS и Android находятся в разработке.',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CyanBar({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-block h-1.5 w-8 rounded-full ${className}`}
      style={{ backgroundColor: '#6bc2ea' }}
    />
  );
}

function getInitials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
}

// ─── StickyNav ────────────────────────────────────────────────────────────────

function StickyNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav
      className={`fixed left-0 right-0 top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur-sm transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : ''
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex shrink-0 items-center gap-2">
            <div
              className="grid h-9 w-9 place-items-center rounded-xl text-sm font-bold text-white"
              style={{ backgroundColor: '#101827' }}
            >
              ВА
            </div>
            <span
              className="hidden text-sm font-semibold sm:block"
              style={{ color: '#101827' }}
            >
              Толмач
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link-hover py-1 text-sm text-gray-600 transition-colors duration-300 hover:text-gray-900"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop right buttons */}
          <div className="hidden items-center gap-3 lg:flex">
            <a
              href="https://t.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link-hover text-sm text-gray-600 transition-colors duration-300 hover:text-gray-900"
            >
              Поддержка
            </a>
            <Link
              to="/login"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium transition-all duration-300 hover:border-[#2463eb] hover:text-[#2463eb]"
            >
              Войти
            </Link>
            <Link
              to="/register"
              className="rounded-lg px-4 py-2 text-sm font-medium text-white transition-all duration-300 hover:opacity-90"
              style={{ backgroundColor: '#2463eb' }}
            >
              Начать бесплатно
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Меню"
          >
            {menuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="border-t border-gray-100 bg-white px-4 pb-4 pt-2 lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3">
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-center text-sm font-medium"
            >
              Войти
            </Link>
            <Link
              to="/register"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg px-4 py-2.5 text-center text-sm font-medium text-white"
              style={{ backgroundColor: '#2463eb' }}
            >
              Начать бесплатно
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── HeroSection ──────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section
      id="hero"
      className="relative flex min-h-screen items-center overflow-hidden pt-16"
      style={{ backgroundColor: '#fafafa' }}
    >
      {/* Background decorative blobs */}
      <div className="pointer-events-none absolute -right-32 -top-40 h-96 w-96 rounded-full bg-blue-50 opacity-70 blur-3xl" />
      <div
        className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full blur-3xl"
        style={{ backgroundColor: 'rgba(107,194,234,0.10)' }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
        {/* Cyan accent row */}
        <div className="mb-6 flex items-center gap-2">
          <CyanBar />
          <span className="text-sm font-medium uppercase tracking-wider text-gray-500">
            Транскрибация речи
          </span>
          <CyanBar />
        </div>

        {/* Main heading */}
        <h1
          className="max-w-4xl font-black leading-tight"
          style={{ fontSize: 'clamp(42px, 7vw, 90px)', color: '#101827' }}
        >
          Распознавание речи
          <br />
          <span style={{ color: '#2463eb', whiteSpace: 'nowrap' }}>
            с AI‑форматированием
          </span>
        </h1>

        {/* Subheading */}
        <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-gray-600 sm:text-xl">
          Загрузите аудиозапись или продиктуйте текст — Толмач
          распознает речь, выделит ключевые факты и оформит готовый документ по
          вашему шаблону.
        </p>

        {/* CTA buttons */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/register"
            className="rounded-xl px-8 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:opacity-90"
            style={{
              backgroundColor: '#2463eb',
              boxShadow: '0 10px 30px rgba(36,99,235,0.25)',
            }}
          >
            Начать бесплатно
          </Link>
          <Link
            to="/login"
            className="rounded-xl border-2 border-gray-300 px-8 py-3.5 text-base font-semibold transition-all duration-300 hover:border-[#2463eb] hover:text-[#2463eb]"
            style={{ color: '#101827' }}
          >
            Войти
          </Link>
        </div>

        {/* Trust indicators */}
        <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-gray-500">
          {[
            'Бесплатный тариф',
            'Без кредитной карты',
            'Настройка за 2 минуты',
          ].map((item) => (
            <span key={item} className="flex items-center gap-1.5">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── HowItWorksSection ────────────────────────────────────────────────────────

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="bg-white py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Как это работает
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            Три простых шага от аудиозаписи до готового документа
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {STEPS.map((step) => (
            <div key={step.number} className="text-center">
              <div
                className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl text-xl font-bold text-white"
                style={{
                  backgroundColor: '#2463eb',
                  boxShadow: '0 8px 24px rgba(36,99,235,0.30)',
                }}
              >
                {step.number}
              </div>
              <h3 className="mb-3 text-xl font-bold" style={{ color: '#101827' }}>
                {step.title}
              </h3>
              <p className="leading-relaxed text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BenefitsSection ──────────────────────────────────────────────────────────

function BenefitsSection() {
  return (
    <section id="benefits" className="py-20 lg:py-32" style={{ backgroundColor: '#fafafa' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="mb-4 flex items-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Преимущества
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-gray-600">
            Всё, что нужно для быстрой и точной транскрибации
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((item) => (
            <div
              key={item.title}
              className="group rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-300 hover:border-[#2463eb]/30 hover:shadow-lg"
            >
              <div className="mb-4 text-3xl">{item.icon}</div>
              <h3
                className="mb-2 text-lg font-bold transition-colors duration-300 group-hover:text-[#2463eb]"
                style={{ color: '#101827' }}
              >
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-gray-600">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ForWhomSection ───────────────────────────────────────────────────────────

function ForWhomSection() {
  return (
    <section id="for-whom" className="bg-white py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Для кого
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            Решение для специалистов, которые ценят своё время
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {AUDIENCES.map((item) => (
            <div
              key={item.role}
              className="rounded-2xl border border-gray-100 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#2463eb]/20 hover:bg-white hover:shadow-xl"
              style={{ backgroundColor: '#fafafa' }}
            >
              <div className="mb-6 text-center text-5xl">{item.emoji}</div>
              <h3
                className="mb-4 text-center text-2xl font-bold"
                style={{ color: '#101827' }}
              >
                {item.role}
              </h3>
              <p className="text-center leading-relaxed text-gray-600">{item.description}</p>
              <div className="mt-6 text-center">
                <Link
                  to="/register"
                  className="text-sm font-medium transition-colors duration-300 hover:underline"
                  style={{ color: '#2463eb' }}
                >
                  Попробовать →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TestimonialsSection ──────────────────────────────────────────────────────

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-20 lg:py-32" style={{ backgroundColor: '#fafafa' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Отзывы
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            Что говорят наши пользователи
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((item) => (
            <div
              key={item.name}
              className="rounded-2xl border border-gray-100 bg-white p-6 transition-shadow duration-300 hover:shadow-lg"
            >
              <div className="mb-4 flex gap-1">
                {Array.from({ length: item.rating }).map((_, i) => (
                  <span key={i} className="text-lg text-amber-400">
                    ★
                  </span>
                ))}
              </div>
              <p className="mb-6 text-sm leading-relaxed text-gray-700">
                «{item.text}»
              </p>
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                  style={{
                    backgroundColor: 'rgba(36,99,235,0.10)',
                    color: '#2463eb',
                  }}
                >
                  {getInitials(item.name)}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#101827' }}>
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-500">{item.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PricingSection ───────────────────────────────────────────────────────────

function PricingSection() {
  return (
    <section id="pricing" className="bg-white py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Тарифы
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            Транскрибация речи — всегда бесплатно. Платите только за AI-обработку.
          </p>
        </div>

        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 xl:grid-cols-4">
          {PRICING.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 ${
                plan.highlight
                  ? 'border-2 md:-mt-4 md:pb-12'
                  : 'border border-gray-200'
              }`}
              style={
                plan.highlight
                  ? {
                      borderColor: '#2463eb',
                      boxShadow: '0 20px 60px rgba(36,99,235,0.15)',
                    }
                  : {}
              }
            >
              {plan.badge && (
                <span
                  className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-xs font-bold text-white"
                  style={{ backgroundColor: '#2463eb' }}
                >
                  {plan.badge}
                </span>
              )}

              <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-500">
                {plan.name}
              </p>

              <div className="mb-1 flex items-baseline gap-2">
                <span
                  className="text-4xl font-black lg:text-5xl"
                  style={{ color: '#101827' }}
                >
                  {plan.price}
                </span>
                {plan.period && (
                  <span className="text-sm text-gray-500">{plan.period}</span>
                )}
              </div>

              <ul className="mb-8 mt-6 space-y-3">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <CheckCircleIcon
                      className="h-4 w-4 shrink-0"
                      style={{ color: '#2463eb' }}
                    />
                    {feature}
                  </li>
                ))}
              </ul>

              {plan.highlight ? (
                <Link
                  to={plan.ctaHref}
                  className="block w-full rounded-xl py-3 text-center text-sm font-semibold text-white transition-all duration-300 hover:opacity-90"
                  style={{ backgroundColor: '#2463eb' }}
                >
                  {plan.cta}
                </Link>
              ) : plan.isExternal ? (
                <a
                  href={plan.ctaHref}
                  className="block w-full rounded-xl border border-gray-300 py-3 text-center text-sm font-semibold transition-all duration-300 hover:border-[#2463eb] hover:text-[#2463eb]"
                  style={{ color: '#101827' }}
                >
                  {plan.cta}
                </a>
              ) : (
                <Link
                  to={plan.ctaHref}
                  className="block w-full rounded-xl border border-gray-300 py-3 text-center text-sm font-semibold transition-all duration-300 hover:border-[#2463eb] hover:text-[#2463eb]"
                  style={{ color: '#101827' }}
                >
                  {plan.cta}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FaqSection ───────────────────────────────────────────────────────────────

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-20 lg:py-32" style={{ backgroundColor: '#fafafa' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <CyanBar />
            <CyanBar />
          </div>
          <h2 className="text-4xl font-bold lg:text-5xl" style={{ color: '#101827' }}>
            Часто задаваемые вопросы
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            Не нашли ответ? Напишите нам в поддержку.
          </p>
        </div>

        <div className="mx-auto max-w-3xl space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl border border-gray-100 bg-white transition-colors duration-200 hover:border-gray-200"
            >
              <button
                className="flex w-full items-center justify-between px-6 py-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span
                  className="pr-4 text-sm font-semibold sm:text-base"
                  style={{ color: '#101827' }}
                >
                  {item.q}
                </span>
                <ChevronDownIcon
                  className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300 ${
                    openIndex === i ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-gray-50 px-6 pb-5 pt-4 text-sm leading-relaxed text-gray-600">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="py-16 text-white" style={{ backgroundColor: '#101827' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-3">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-xl border border-gray-600 text-sm font-bold">
                Т
              </div>
              <span className="font-semibold">Толмач</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-gray-400">
              Транскрибация речи с AI-форматированием документов.
              Для специалистов, которые ценят своё время.
            </p>
          </div>

          {/* Product links */}
          <div>
            <p className="mb-4 font-semibold">Продукт</p>
            <ul className="space-y-2">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <p className="mb-4 font-semibold">Поддержка</p>
            <ul className="space-y-2">
              <li>
                <a
                  href="mailto:support@example.com"
                  className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
                >
                  support@example.com
                </a>
              </li>
              <li>
                <a
                  href="https://t.me/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
                >
                  Telegram
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
                >
                  Политика конфиденциальности
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-8 h-px bg-gray-800" />

        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Толмач. Все права защищены.
          </p>
          <div className="flex gap-4">
            <Link
              to="/login"
              className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
            >
              Войти
            </Link>
            <Link
              to="/register"
              className="text-sm text-gray-400 transition-colors duration-200 hover:text-white"
            >
              Зарегистрироваться
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

// <!-- GRACE:CONTRACT function="InfoPage" -->
export default function InfoPage() {
  return (
    <>
      <StickyNav />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <BenefitsSection />
        <ForWhomSection />
        <TestimonialsSection />
        <PricingSection />
        <FaqSection />
      </main>
      <Footer />
    </>
  );
}
// <!-- GRACE:MODULE_END name="ui_landing" -->
